import * as React from 'react';

import { useLongPoll } from './useLongPoll';
import type { SSEConnectionChange, UpdateEvents } from './useSSEEvent';
import { useSSEEvent } from './useSSEEvent';

export type LiveDataMode = 'sse' | 'long-polling';

/**
 * Single live-data entry point for SSE consumers, with an opt-in long-polling
 * fallback for networks that block long-lived connections.
 *
 * Owns only the transport `mode` and delegates all data handling to the
 * consumer's `onMessage` (forwarding `eventType` unchanged), so it stays
 * compatible with every consumer shape (snapshot, chat accumulation, report
 * append) and preserves each consumer's own data/error/side-effects.
 *
 * - No `fallback` → SSE only; retry exhaustion surfaces via `onError` exactly
 *   as before.
 * - `fallback` provided → on retry exhaustion the mode flips **one-way** to
 *   long-polling and stays there until the hook remounts.
 */
export const useLiveData = <T>({
  url,
  onMessage,
  onError,
  onConnectionChange,
  enabled = true,
  retryMs,
  maxRetries,
  fallback,
}: {
  url: string;
  onMessage: (data: T, eventType: UpdateEvents) => void;
  onError?: (error: string) => void;
  /** Forwarded to the SSE transport; only fires while in SSE mode. */
  onConnectionChange?: (change: SSEConnectionChange) => void;
  enabled?: boolean;
  retryMs?: number;
  maxRetries?: number;
  fallback?: {
    fetchSnapshot: (signal?: AbortSignal) => Promise<T>;
    intervalMs?: number;
  };
}): { mode: LiveDataMode } => {
  const [mode, setMode] = React.useState<LiveDataMode>('sse');

  // One-way transport switch: once SSE exhausts its retries we stay on
  // long-polling until the hook remounts.
  const handleExhausted = React.useCallback((): void => {
    setMode('long-polling');
  }, []);

  useSSEEvent<T>({
    url,
    onMessage,
    onError,
    onConnectionChange,
    onExhausted: fallback ? handleExhausted : undefined,
    enabled: enabled && mode === 'sse',
    retryMs,
    maxRetries,
  });

  useLongPoll<T>({
    // Never invoked while polling is disabled; a stable never-resolving default
    // keeps the hook order intact when no fallback is configured.
    fetchSnapshot:
      fallback?.fetchSnapshot ??
      (() =>
        new Promise<T>(() => {
          /* fallback disabled: never resolves and is never invoked */
        })),
    onMessage,
    onError,
    enabled: enabled && mode === 'long-polling' && !!fallback,
    intervalMs: fallback?.intervalMs,
  });

  return { mode };
};
